import { jsPDF } from 'jspdf';

/**
 * Build a branded multi-page PDF eligibility report from the user's quiz state.
 *
 * @param {object} args
 * @param {string} args.userName
 * @param {object} args.data - raw quiz answers { qualification, passingYear, gpa, englishTest, englishScore, funds, destination }
 * @param {Array}  args.results - output of evaluateEligibility(data)
 * @param {object} args.dictionaries - lookup arrays { qualificationOptions, gpaOptions, fundsOptions, countryOptions }
 * @returns {jsPDF} the generated jsPDF document instance (call .save(filename) or .output('blob'))
 */
export function buildEligibilityPdf({ userName, data, results, dictionaries }) {
  const { qualificationOptions, gpaOptions, fundsOptions, countryOptions } = dictionaries;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  let y = 50;

  // Header band
  doc.setFillColor(0, 48, 135);
  doc.rect(0, 0, pageWidth, 80, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('ZAFCO Study Abroad', marginX, 38);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Official Visa Eligibility Report', marginX, 58);
  doc.setFontSize(9);
  doc.text(
    new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }),
    pageWidth - marginX - 80,
    58
  );

  y = 110;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(`Prepared for: ${userName || 'Prospective Student'}`, marginX, y);
  y += 22;

  // Profile snapshot
  doc.setDrawColor(197, 160, 40);
  doc.setLineWidth(1.5);
  doc.line(marginX, y, marginX + 40, y);
  y += 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 48, 135);
  doc.text('Your Profile Snapshot', marginX, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const qualLabel = qualificationOptions.find((q) => q.value === data.qualification)?.label || '—';
  const gpaLabel = gpaOptions.find((g) => g.value === data.gpa)?.label || '—';
  const englishLabel =
    data.englishTest === 'none' ? 'Not Taken' : `${data.englishTest.toUpperCase()} ${data.englishScore}`;
  const fundsLabel = fundsOptions.find((f) => f.value === data.funds)?.label || '—';
  [
    ['Highest Qualification', qualLabel],
    ['Year of Completion', data.passingYear || '—'],
    ['Academic Score', gpaLabel],
    ['English Proficiency', englishLabel],
    ['Available Funds', fundsLabel],
    ['Preferred Destination', countryOptions.find((c) => c.value === data.destination)?.label || '—'],
  ].forEach(([k, v]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${k}:`, marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(v), marginX + 140, y);
    y += 16;
  });

  // Country scorecards
  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 48, 135);
  doc.text('Country-wise Eligibility Assessment', marginX, y);
  y += 18;

  results.forEach((r) => {
    if (y > 720) {
      doc.addPage();
      y = 60;
    }
    const badgeMap = {
      high: 'HIGH ELIGIBILITY',
      medium: 'MEDIUM ELIGIBILITY',
      action_required: 'ACTION REQUIRED',
    };
    // Lookup maps replace nested ternaries: cleaner + adding new statuses
    // becomes a one-line edit.
    const badgeColorMap = {
      high: [34, 197, 94],
      medium: [245, 158, 11],
      action_required: [239, 68, 68],
    };
    const badgeColor = badgeColorMap[r.overall] || badgeColorMap.action_required;

    // Country header bar
    doc.setFillColor(245, 247, 250);
    doc.rect(marginX, y - 4, pageWidth - marginX * 2, 22, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(r.country, marginX + 8, y + 12);
    doc.setFillColor(...badgeColor);
    doc.roundedRect(pageWidth - marginX - 130, y, 122, 14, 4, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text(badgeMap[r.overall], pageWidth - marginX - 125, y + 10);
    y += 28;

    // Checks
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    // Per-check status visual maps (replaces inline ternary chains)
    const symbolMap = { pass: '+ ', caution: '! ', fail: 'x ' };
    const symColorMap = {
      pass: [22, 163, 74],
      caution: [217, 119, 6],
      fail: [220, 38, 38],
    };
    r.checks.forEach((c) => {
      if (y > 760) {
        doc.addPage();
        y = 60;
      }
      const symbol = symbolMap[c.status] || symbolMap.fail;
      const symColor = symColorMap[c.status] || symColorMap.fail;
      doc.setTextColor(...symColor);
      doc.setFont('helvetica', 'bold');
      doc.text(symbol, marginX + 8, y);
      doc.setTextColor(30, 41, 59);
      doc.text(c.label, marginX + 22, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      const wrapped = doc.splitTextToSize(c.note, pageWidth - marginX * 2 - 30);
      doc.text(wrapped, marginX + 22, y + 12);
      y += 14 + wrapped.length * 11;
    });
    y += 8;
  });

  // Footer CTA
  if (y > 720) {
    doc.addPage();
    y = 60;
  }
  y += 10;
  doc.setFillColor(0, 48, 135);
  doc.rect(marginX, y, pageWidth - marginX * 2, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Next Step: Book a Free Counselling Session', marginX + 16, y + 24);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Our certified counsellors will review your profile in detail and build', marginX + 16, y + 42);
  doc.text(
    'a personalized roadmap. Call +91-9990533327 or visit zafco.in/study-abroad',
    marginX + 16,
    y + 55
  );

  // Footer line
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(
    '© ZAFCO Study Abroad — Confidential. This is an indicative assessment based on user-provided data.',
    marginX,
    pageHeight - 20
  );

  return doc;
}
